<script>
  import { onMount, afterUpdate, createEventDispatcher } from 'svelte';
  import Chart from 'chart.js/auto';
  import WeightForm from './WeightForm.svelte';

  export let records = [];

  const dispatch = createEventDispatcher();
  let showAddDialog = false;

  let canvas;
  let chartInstance;

  function formatDateLabel(date) {
    const d = new Date(date);
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const year = d.getFullYear();

    // Check if we need to show year (if data spans multiple years)
    if (records.length > 1) {
      const years = new Set(records.map(r => new Date(r.date).getFullYear()));
      if (years.size > 1) {
        return `${year}/${month}/${day}`;
      }
    }

    return `${month}/${day}`;
  }

  function renderChart() {
    if (chartInstance) {
      chartInstance.destroy();
    }

    if (!records || records.length === 0) {
      return;
    }

    // Sort records by date in ascending order for the chart
    const sortedRecords = [...records].sort((a, b) => new Date(a.date) - new Date(b.date));

    const labels = sortedRecords.map(record => formatDateLabel(record.date));
    const data = sortedRecords.map(record => record.weight);

    // Calculate min/max for better Y-axis scaling
    const minWeight = Math.min(...data);
    const maxWeight = Math.max(...data);
    const range = maxWeight - minWeight;
    const padding = range > 0 ? range * 0.1 : 5; // 10% padding or 5kg if all weights are the same

    chartInstance = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: '體重 (kg)',
          data: data,
          borderColor: 'rgb(79, 70, 229)', // primary-600
          backgroundColor: 'rgba(79, 70, 229, 0.1)',
          tension: 0.3,
          fill: true,
          pointRadius: sortedRecords.length === 1 ? 6 : 4,
          pointHoverRadius: sortedRecords.length === 1 ? 8 : 6,
          pointBackgroundColor: 'rgb(79, 70, 229)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        scales: {
          x: {
            title: {
              display: false,
              text: '日期',
              color: 'rgb(55, 65, 81)',
              font: {
                size: 12,
                weight: '500'
              }
            },
            ticks: {
              color: 'rgb(107, 114, 128)',
              maxRotation: 45,
              minRotation: 0
            },
            grid: {
              display: false
            }
          },
          y: {
            title: {
              display: false,
              text: '體重 (kg)',
              color: 'rgb(55, 65, 81)',
              font: {
                size: 12,
                weight: '500'
              }
            },
            ticks: {
              color: 'rgb(107, 114, 128)'
            },
            beginAtZero: false,
            min: Math.max(0, minWeight - padding),
            max: maxWeight + padding,
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(17, 24, 39, 0.9)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: 'rgb(79, 70, 229)',
            borderWidth: 1,
            padding: 12,
            displayColors: false,
            callbacks: {
              label: function(context) {
                return `體重: ${context.parsed.y} kg`;
              }
            }
          }
        }
      }
    });
  }

  onMount(() => {
    renderChart();
  });

  afterUpdate(() => {
    renderChart();
  });

  function handleSave() {
    showAddDialog = false;
    dispatch('reload');
  }

  function handleClose() {
    showAddDialog = false;
  }
</script>

<div class="card">
  <div class="flex justify-between items-center mb-4">
    <h2 class="text-xl font-semibold text-gray-800">體重變化趨勢</h2>
    <button
      on:click={() => showAddDialog = true}
      class="btn btn-primary text-sm py-1.5 px-3"
      title="新增體重記錄"
    >
      + 新增記錄
    </button>
  </div>
  {#if records && records.length > 0}
    <div class="h-64 sm:h-80">
      <canvas bind:this={canvas}></canvas>
    </div>
    {#if records.length === 1}
      <p class="text-sm text-gray-500 text-center mt-4">
        目前只有一筆記錄，繼續記錄以查看趨勢變化
      </p>
    {/if}
  {:else}
    <div class="text-center py-12">
      <div class="text-6xl mb-4">📊</div>
      <h3 class="text-lg font-semibold text-gray-800 mb-2">尚未有體重記錄</h3>
      <p class="text-gray-600">點擊右上角的「新增記錄」按鈕開始記錄體重</p>
    </div>
  {/if}
</div>

{#if showAddDialog}
  <WeightForm
    mode="create"
    on:save={handleSave}
    on:close={handleClose}
  />
{/if}
